/**
 * Typed client for the avia API (server/api, Rust/Axum).
 *
 * Every call rides the httpOnly session cookie (`credentials: 'include'`) and
 * surfaces the server's stable `{code, message}` body as an {@link ApiError}.
 * The one case that is not an error is a stale plan write: PUT /plans/:id may
 * answer 409 `version_conflict` with the server's copy of the document, which
 * `updatePlan` hands back so the editor can keep the local work and offer to
 * take the server copy.
 */

import { parseAircraftProfile, type AircraftProfile } from '@avia/core';

import { hasKey, type T, type Vars } from './i18n';
import { parseRoute, type FeatureLayer, type RouteDoc } from './route';
import { parsePlanMeta, type PlanMeta } from './store';

/** Same-origin in production; in dev Vite proxies /api to http://localhost:8080. */
const BASE = '/api';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
  /** No session (or it expired) — the caller should fall back to local-only mode. */
  get unauthenticated(): boolean {
    return this.status === 401 || this.code === 'unauthenticated';
  }
}

export interface Me {
  id: string;
  email: string;
  display_name: string | null;
}

export interface Aircraft {
  id: string;
  registration: string;
  type_code: string | null;
  /** `aircraft.profile` v1, parsed — see `@avia/core`'s `aircraftProfile.ts` */
  profile: AircraftProfile;
  updated_at: string;
}

export interface AircraftInput {
  registration: string;
  type_code?: string | null;
  profile?: AircraftProfile;
}

/** The plan document: LBA field id → value, plus the pilot's manual-override set. */
export interface Plan {
  id: string;
  title: string;
  is_template: boolean;
  aircraft_id: string | null;
  values: Record<string, string>;
  manual: string[];
  /** the map's waypoint list (migration 0004); null when the plan has no route */
  route: RouteDoc | null;
  /** `plans.meta` (migration 0005): the derived-tag sets — which cells the aircraft profile wrote */
  meta: PlanMeta | null;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface PlanSummary {
  id: string;
  title: string;
  is_template: boolean;
  aircraft_id: string | null;
  route: RouteDoc | null;
  version: number;
  updated_at: string;
  von: string | null;
  nach: string | null;
  datum: string | null;
}

export interface PlanInput {
  title: string;
  is_template: boolean;
  aircraft_id?: string | null;
  values: Record<string, string>;
  manual: string[];
  /**
   * PUT semantics are the server's: **omitting the key keeps the server's copy**,
   * `null` clears the route, an object replaces it. The editor owns the whole
   * plan and therefore always sends it explicitly.
   */
  route?: RouteDoc | null;
  /**
   * `plans.meta` (migration 0005). Sent explicitly on every save, like `route`:
   * the editor owns the whole document, and a tag set that is not sent is a tag
   * set that comes back wrong after a reload. An API that does not know the key
   * yet ignores it (`PlanInput` tolerates unknown fields), so the client can ship first.
   */
  meta?: PlanMeta | null;
  /** required on update: the version the client last saw */
  version?: number;
}

export interface Share {
  token: string;
  url_path: string;
  can_edit: boolean;
}

interface ErrorBody {
  code?: unknown;
  message?: unknown;
}

async function readBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function errorFrom(status: number, body: unknown): ApiError {
  const b = (body ?? {}) as ErrorBody;
  const code = typeof b.code === 'string' ? b.code : statusCode(status);
  const message = typeof b.message === 'string' ? b.message : 'HTTP ' + status;
  return new ApiError(status, code, message);
}

/** A body without a `code` still gets a stable one, so the UI can translate it. */
function statusCode(status: number): string {
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status >= 500) return 'internal';
  return 'http_' + status;
}

/**
 * Codes whose server message names what exactly is wrong ("Password must be at
 * least 10 characters"). The catalogue sentence alone would drop the one thing
 * the pilot needs, so the server's detail is appended to it.
 */
const DETAILED = new Set(['invalid', 'conflict']);

/**
 * The message to show for a failed call: the catalogue text for the server's
 * stable code, and only where we have none, whatever the server said.
 */
export function apiMessage(t: T, e: unknown): string {
  if (e instanceof ApiError) {
    const key = 'err.' + e.code;
    if (hasKey(key)) {
      const text = t(key);
      const detail = e.message.startsWith('HTTP ') ? '' : e.message;
      return DETAILED.has(e.code) && detail ? text + ' ' + detail : text;
    }
    if (e.code.startsWith('http_')) return t('err.http', { status: e.status });
    return e.message || t('err.internal');
  }
  return e instanceof Error ? e.message : String(e);
}

/* ---------------- expired sessions ---------------- */

type SessionListener = () => void;
const sessionListeners = new Set<SessionListener>();

/**
 * Called when a request that needed a session was answered "no session". The
 * session cookie is httpOnly and lasts 90 days, so the app cannot see it expire —
 * a 401 out of an ordinary call is the only notice there is, and swallowing it is
 * what makes autosave look like it silently stopped working.
 *
 * Returns the unsubscribe function. See `session.tsx`, the only subscriber.
 */
export function onSessionExpired(fn: SessionListener): () => void {
  sessionListeners.add(fn);
  return () => {
    sessionListeners.delete(fn);
  };
}

/**
 * Exactly one thing counts as an expired session: the code `unauthenticated` on a
 * call that is not part of signing in. `/auth/*` is excluded on purpose — `/auth/me`
 * answers 401 for every signed-out visitor, and a wrong password on `/auth/login`
 * or `/auth/password` is 401 `bad_credentials`, which is the user's typo and not a
 * dead session.
 */
function noteSessionState(path: string, e: ApiError): void {
  if (!(e.status === 401 && e.code === 'unauthenticated')) return;
  if (path.startsWith('/auth/')) return;
  for (const fn of [...sessionListeners]) fn();
}

/**
 * What a form has to show below itself: either one of our own catalogue keys (the
 * rules the client checks before asking, "the two passwords differ") or an error
 * that was thrown. Kept as data rather than as a finished string so that switching
 * language re-renders the message.
 */
export type FormError = { key: string; vars?: Vars } | { err: unknown } | null;

export function formMessage(t: T, e: FormError): string {
  if (!e) return '';
  return 'key' in e ? t(e.key, e.vars) : apiMessage(t, e.err);
}

async function request<T>(path: string, method: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method, credentials: 'include' };
  if (body !== undefined) {
    init.headers = { 'content-type': 'application/json' };
    init.body = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(BASE + path, init);
  } catch {
    throw new ApiError(0, 'network', 'network error');
  }
  const payload = await readBody(res);
  if (!res.ok) {
    const e = errorFrom(res.status, payload);
    noteSessionState(path, e);
    throw e;
  }
  return payload as T;
}

/** Normalise a JSONB plan document — the column is schemaless, the editor is not. */
function normalisePlan(raw: unknown): Plan {
  const p = raw as Plan & { values?: unknown; manual?: unknown };
  const values: Record<string, string> = {};
  const rawValues = p.values;
  if (rawValues && typeof rawValues === 'object' && !Array.isArray(rawValues)) {
    for (const [k, v] of Object.entries(rawValues as Record<string, unknown>)) {
      if (v == null) continue;
      values[k] = String(v);
    }
  }
  const manual = Array.isArray(p.manual) ? p.manual.filter((x): x is string => typeof x === 'string') : [];
  return {
    ...p,
    values,
    manual,
    route: parseRoute((p as { route?: unknown }).route),
    meta: parsePlanMeta((p as { meta?: unknown }).meta),
  };
}

/** The aircraft's `profile` column is schemaless jsonb; the app is not. */
function normaliseAircraft(raw: unknown): Aircraft {
  const a = raw as Aircraft & { profile?: unknown };
  return { ...a, profile: parseAircraftProfile(a.profile) };
}

/** A plan summary's route is the same schemaless column — parse it the same way. */
function normaliseSummary(raw: unknown): PlanSummary {
  const p = raw as PlanSummary & { route?: unknown };
  return { ...p, route: parseRoute(p.route) };
}

/* ---------------- auth ---------------- */

export const register = (email: string, password: string, displayName?: string): Promise<Me> =>
  request<Me>('/auth/register', 'POST', {
    email,
    password,
    display_name: displayName && displayName.trim() ? displayName.trim() : null,
  });

export const login = (email: string, password: string): Promise<Me> =>
  request<Me>('/auth/login', 'POST', { email, password });

export const logout = (): Promise<void> => request<void>('/auth/logout', 'POST', {});

export const me = (): Promise<Me> => request<Me>('/auth/me', 'GET');

/** GET /auth/me, but a missing session is `null` rather than a throw. */
export async function currentUser(): Promise<Me | null> {
  try {
    return await me();
  } catch (e) {
    if (e instanceof ApiError && (e.unauthenticated || e.status === 0)) return null;
    throw e;
  }
}

/**
 * The answer of the three password endpoints: `{ok:true}`. `debug_token` exists
 * only when the server runs with `AVIA_TEST_RESET_ECHO=1` (a test hook, never a
 * deployed environment) and is never read by the app.
 */
export interface OkBody {
  ok: boolean;
  debug_token?: string;
}

/**
 * Ask for a reset link. **Always resolves 200 `{ok:true}`** — for an address with
 * no account, and for one that is not an address at all. The UI must therefore say
 * "if an account exists …" and never "we have sent you a mail": anything else turns
 * this endpoint into a membership oracle. See `server/api/src/auth.rs`.
 */
export const forgotPassword = (email: string): Promise<OkBody> =>
  request<OkBody>('/auth/forgot', 'POST', { email });

/**
 * Spend a reset token. 400 `invalid_token` when the link is unknown, already used
 * or older than 60 minutes; 422 `invalid` when the new password is too short.
 * On success **every** session of that user is gone, including this browser's.
 */
export const resetPassword = (token: string, password: string): Promise<OkBody> =>
  request<OkBody>('/auth/reset', 'POST', { token, password });

/**
 * Change the password of the signed-in account. 401 `bad_credentials` means the
 * *current* password was wrong — not that the session died. The calling session
 * survives; every other device is signed out.
 */
export const changePassword = (current: string, next: string): Promise<OkBody> =>
  request<OkBody>('/auth/password', 'POST', { current, new: next });

/* ---------------- aircraft ---------------- */

export async function listAircraft(): Promise<Aircraft[]> {
  const rows = await request<unknown[]>('/aircraft', 'GET');
  return (Array.isArray(rows) ? rows : []).map(normaliseAircraft);
}

export async function createAircraft(a: AircraftInput): Promise<Aircraft> {
  return normaliseAircraft(await request<unknown>('/aircraft', 'POST', { profile: {}, type_code: null, ...a }));
}

export async function updateAircraft(id: string, a: AircraftInput): Promise<Aircraft> {
  return normaliseAircraft(
    await request<unknown>('/aircraft/' + encodeURIComponent(id), 'PUT', { profile: {}, type_code: null, ...a }),
  );
}

export const deleteAircraft = (id: string): Promise<void> =>
  request<void>('/aircraft/' + encodeURIComponent(id), 'DELETE');

/* ---------------- plans ---------------- */

export async function listPlans(templates?: boolean): Promise<PlanSummary[]> {
  const q = templates === undefined ? '' : '?templates=' + String(templates);
  const rows = await request<unknown[]>('/plans' + q, 'GET');
  return (Array.isArray(rows) ? rows : []).map(normaliseSummary);
}

export async function createPlan(input: PlanInput): Promise<Plan> {
  return normalisePlan(await request<unknown>('/plans', 'POST', input));
}

export async function getPlan(id: string): Promise<Plan> {
  return normalisePlan(await request<unknown>('/plans/' + encodeURIComponent(id), 'GET'));
}

export type UpdateResult =
  | { ok: true; plan: Plan }
  /** 409: someone else wrote first. `server` is their copy; the local document is untouched. */
  | { ok: false; conflict: true; server: Plan };

/**
 * PUT a plan with the version last seen. A stale write comes back as
 * `{ok:false, conflict:true, server}` instead of throwing, because losing a
 * flight plan to a silent overwrite is the one failure mode this app must not have.
 */
export async function updatePlan(id: string, input: PlanInput & { version: number }): Promise<UpdateResult> {
  const init: RequestInit = {
    method: 'PUT',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  };
  const path = '/plans/' + encodeURIComponent(id);
  let res: Response;
  try {
    res = await fetch(BASE + path, init);
  } catch {
    throw new ApiError(0, 'network', 'network error');
  }
  const payload = await readBody(res);
  if (res.status === 409) {
    const body = payload as { code?: unknown; server?: unknown };
    if (body && body.code === 'version_conflict' && body.server) {
      return { ok: false, conflict: true, server: normalisePlan(body.server) };
    }
  }
  if (!res.ok) {
    const e = errorFrom(res.status, payload);
    // the editor's autosave is the call most likely to be the first to notice
    noteSessionState(path, e);
    throw e;
  }
  return { ok: true, plan: normalisePlan(payload) };
}

export const deletePlan = (id: string): Promise<void> =>
  request<void>('/plans/' + encodeURIComponent(id), 'DELETE');

export async function instantiatePlan(id: string): Promise<Plan> {
  return normalisePlan(await request<unknown>('/plans/' + encodeURIComponent(id) + '/instantiate', 'POST', {}));
}

export const sharePlan = (id: string): Promise<Share> =>
  request<Share>('/plans/' + encodeURIComponent(id) + '/share', 'POST', {});

export async function getSharedPlan(token: string): Promise<Plan> {
  return normalisePlan(await request<unknown>('/shared/' + encodeURIComponent(token), 'GET'));
}

export const health = (): Promise<string> => request<string>('/health', 'GET');

/* ---------------- aviation data (openAIP mirror, public) ---------------- */

/**
 * One hit of `GET /api/geo/search`. `id` is the openAIP `_id` — the same id the
 * vector tiles carry, so a search result and a clicked feature are the same
 * object. `extra` drops null-valued keys, exactly like the tiles do.
 */
export interface GeoHit {
  layer: 'airports' | 'navaids' | 'reporting_points';
  id: string;
  name: string;
  ident: string | null;
  kind: string;
  lat: number;
  lon: number;
  country: string;
  elev_ft: number | null;
  extra: Record<string, unknown>;
}

export interface GeoFeature {
  layer: FeatureLayer;
  id: string;
  name: string;
  country: string;
  kind: string;
  lat: number;
  lon: number;
  props: Record<string, unknown>;
  geometry: unknown;
  raw: Record<string, unknown>;
}

export interface SearchOptions {
  limit?: number;
  /** ties are broken by great-circle distance to this point (the map centre) */
  near?: { lat: number; lon: number };
  signal?: AbortSignal;
}

/** `q` must be 2–40 characters; the server caps `limit` at 50 rather than erroring. */
export async function searchGeo(q: string, opts: SearchOptions = {}): Promise<GeoHit[]> {
  const p = new URLSearchParams({ q });
  if (opts.limit) p.set('limit', String(opts.limit));
  if (opts.near) p.set('near', opts.near.lat.toFixed(4) + ',' + opts.near.lon.toFixed(4));
  return requestSignal<GeoHit[]>('/geo/search?' + p.toString(), opts.signal);
}

/** `geometry: false` skips the polygon when the client already has it from the tiles. */
export const getFeature = (layer: FeatureLayer, id: string, geometry = true): Promise<GeoFeature> =>
  request<GeoFeature>(
    '/geo/feature/' + encodeURIComponent(layer) + '/' + encodeURIComponent(id) + (geometry ? '' : '?geometry=0'),
    'GET',
  );

/* ---------------- airspace check ---------------- */

export interface RouteCheckPoint {
  lat: number;
  lon: number;
  alt_ft?: number | null;
}

export interface CheckedAirspace {
  id: string;
  name: string;
  kind: string;
  icao_class: string | null;
  lower_ft: number | null;
  lower_ref: string | null;
  lower_raw: string | null;
  upper_ft: number | null;
  upper_ref: string | null;
  upper_raw: string | null;
  entry_nm: number | null;
  exit_nm: number | null;
}

export interface CheckedLeg {
  leg: number;
  from: { lat: number; lon: number };
  to: { lat: number; lon: number };
  dist_nm: number;
  alt_band_ft: [number, number] | null;
  airspaces: CheckedAirspace[];
}

/**
 * The answer of `POST /api/route/check` — an object, not a bare array: `total_nm`
 * and `assumptions` belong to the route, not to any one leg. `assumptions` is a
 * list of stable codes (`great_circle_leg`, `gnd_as_zero`, …), translated by the
 * client; it must be shown, because the answer is a hint and not a clearance.
 */
export interface RouteCheck {
  legs: CheckedLeg[];
  total_nm: number;
  assumptions: string[];
}

export function checkRoute(
  waypoints: RouteCheckPoint[],
  opts: { defaultAltFt?: number; includeEnroute?: boolean; signal?: AbortSignal } = {},
): Promise<RouteCheck> {
  const body: Record<string, unknown> = { waypoints };
  if (opts.defaultAltFt != null) body['default_alt_ft'] = opts.defaultAltFt;
  // includeEnroute keeps FIR/UIR/FIS/ACC/UTA — the blanket sectors every European route is
  // inside, dropped by default because they bury the CTRs and TMAs that need a call
  return requestSignal<RouteCheck>(
    '/route/check' + (opts.includeEnroute ? '?include_enroute=1' : ''),
    opts.signal,
    body,
  );
}

/* ---------------- weather (NOAA proxy, public) ---------------- */

/**
 * One station's METAR as `GET /api/wx/metar` renames NOAA's decoded fields.
 *
 * `raw` is the bulletin as transmitted and is **never** re-derived from the
 * decoded fields (see `server/api/src/wx.rs`): it is what the pilot briefs on and
 * what `parseMetarToAtis` reads. The decoded halves are typed the way NOAA types
 * them — `wdir` is a number *or* the string `"VRB"`, `visib` statute miles as a
 * number *or* a string (`"6+"`) — and are passed straight through.
 */
export interface WxMetar {
  raw: string;
  /** ISO-8601 UTC, e.g. `2026-08-17T10:20:00Z` */
  obs_time: string | null;
  wdir: number | string | null;
  wspd: number | null;
  wgst: number | null;
  visib: number | string | null;
  temp: number | null;
  dewp: number | null;
  altim_hpa: number | null;
  clouds: { cover: string; base_ft: number | null }[];
  fltcat: string | null;
}

export interface WxTaf {
  raw: string;
  issue_time: string | null;
  valid_from: string | null;
  valid_to: string | null;
  /** NOAA's decoded forecast periods, verbatim — not read by this app */
  fcsts: unknown;
}

export interface WxStation {
  id: string;
  /** null when NOAA publishes nothing for that station (not every field reports) */
  metar: WxMetar | null;
  taf: WxTaf | null;
  /** `'unavailable'` when the refresh failed; the response is still 200 */
  error?: string;
}

export interface WxResponse {
  stations: WxStation[];
  /** the exact attribution string, e.g. `NOAA aviationweather.gov` */
  source: string;
  fetched_at: string;
}

/** 1–8 ICAO ids. Public — no session needed, so this works on a shared link too. */
export const fetchMetar = (ids: string[], signal?: AbortSignal): Promise<WxResponse> =>
  requestSignal<WxResponse>('/wx/metar?ids=' + encodeURIComponent(ids.join(',')), signal);

/**
 * One station near a point, as `GET /api/wx/nearby` returns it.
 *
 * `metar` and `taf` are the very same objects `/wx/metar` gives, which is the whole
 * contract: one decoder and one renderer serve both endpoints. `dist_nm` and
 * `bearing_deg` are measured from the point asked about — a true bearing, 0 = north.
 */
export interface WxNearbyStation {
  id: string;
  /** openAIP's name for the code, or null when the mirror has no row for it */
  name: string | null;
  lat: number;
  lon: number;
  dist_nm: number;
  bearing_deg: number;
  metar: WxMetar | null;
  taf: WxTaf | null;
  error?: string;
}

export interface WxNearbyResponse {
  origin: { lat: number; lon: number };
  /** the radius actually searched — the server's default is not the client's to assume */
  radius_nm: number;
  stations: WxNearbyStation[];
  /** `'unavailable'`: the box could not be looked up and the server holds no cache for it */
  error?: string;
  source: string;
  fetched_at: string;
}

/**
 * The nearest stations NOAA publishes anything for, within `radius_nm` (≤ 100) of a
 * point. Public, like `/wx/metar`. `limit` is capped at 3 and both caps are 422s
 * rather than clamps, so nothing here quietly asks a different question.
 */
export function fetchNearby(
  lat: number,
  lon: number,
  opts: { radiusNm?: number; limit?: number; signal?: AbortSignal } = {},
): Promise<WxNearbyResponse> {
  const q = new URLSearchParams({ lat: lat.toFixed(4), lon: lon.toFixed(4) });
  if (opts.radiusNm != null) q.set('radius_nm', String(Math.round(opts.radiusNm)));
  if (opts.limit != null) q.set('limit', String(opts.limit));
  return requestSignal<WxNearbyResponse>('/wx/nearby?' + q.toString(), opts.signal);
}

/* ---------------- winds aloft (Open-Meteo proxy, public) ---------------- */

/** One point to ask about: where, and at what altitude. `alt_ft` is 0..20 000. */
export interface WindsQueryPoint {
  lat: number;
  lon: number;
  alt_ft: number;
}

/**
 * One answered point. Everything but `lat`/`lon`/`alt_ft` is optional, because a
 * point the upstream model could not serve comes back as `{…, error:
 * "unavailable"}` — the response stays 200, one dead point must not lose the
 * other twenty-three.
 *
 * `wv` is the `ddd/kk` string the LBA form's W/V column stores, made by the
 * server (north is `360`, never `000`, and never `VRB`). It is written into the
 * form verbatim: re-deriving it from `wdir_true`/`wspd_kt` here would be a second
 * implementation of a format the server already pinned down.
 */
export interface WindsPoint {
  lat: number;
  lon: number;
  alt_ft: number;
  wdir_true?: number;
  wspd_kt?: number;
  temp_c?: number;
  /** which model served this point (`icon_d2`, `icon_eu`, `best_match`) */
  model?: string;
  /** the forecast hour used, e.g. `2026-08-17T12:00Z` — the VALID hour, not the model run */
  source_hour?: string;
  wv?: string;
  /** `'unavailable'`: upstream failed or has no data here */
  error?: string;
}

export interface WindsResponse {
  /** the time asked for, echoed */
  at: string;
  /** the model that served the most points */
  model: string;
  /** aligned with the request's point list, one entry each */
  points: WindsPoint[];
  /** the required attribution string, e.g. `Open-Meteo (CC BY 4.0) · DWD ICON` */
  source: string;
  fetched_at: string;
}

/**
 * 1–24 points, `at` within `now-2h .. now+5d` (a value outside that is a 422, not
 * a clamp — see `winds.ts`, which is what keeps the plan's date inside it).
 * Public — no session needed.
 */
export function fetchWinds(
  points: readonly WindsQueryPoint[],
  at?: string,
  signal?: AbortSignal,
): Promise<WindsResponse> {
  const list = points
    .map(p => p.lat.toFixed(4) + ',' + p.lon.toFixed(4) + ',' + String(Math.round(p.alt_ft)))
    .join(';');
  const q = new URLSearchParams({ points: list });
  if (at) q.set('at', at);
  return requestSignal<WindsResponse>('/wx/winds?' + q.toString(), signal);
}

/* ---------------- terrain (PMTiles DEM + the openAIP obstacles, public) ---------------- */

/** One along-track sample. `elev_ft` is null where the archive has **no tile**. */
export interface TerrainSample {
  nm: number;
  elev_ft: number | null;
}

/**
 * One obstacle inside the corridor.
 *
 * `exact` is not decoration: openAIP knows a height for ~15 % of obstacles. Where
 * it does not, `elev_ft` is the **ground** under the obstacle and the real top is
 * higher by an unknown amount — which is why the UI prints "≥" in front of it.
 */
export interface TerrainObstacle {
  id: string;
  name: string;
  /** openAIP's obstacle type, e.g. `tower` */
  type: string;
  elev_ft: number | null;
  exact: boolean;
  dist_nm: number;
}

/** One leg of the profile. `leg` is **0-based**, exactly as `/api/route/check` numbers them. */
export interface TerrainLeg {
  leg: number;
  dist_nm: number;
  samples: TerrainSample[];
  max_terrain_ft: number | null;
  max_terrain_at_nm: number | null;
  obstacles: TerrainObstacle[];
  max_obstacle_ft: number | null;
  /** `ceil100(max(terrain, obstacle) + 1000/2000)`; null when the leg is outside the archive */
  msa_ft: number | null;
  /** `flat` | `mountain` — which clearance was applied, and it follows the TERRAIN */
  msa_rule: string;
}

/**
 * The answer of `POST /api/terrain/profile`. Like the airspace check it is an
 * object rather than a bare array: `msa_ft`, `step_nm` and `assumptions` belong
 * to the route, not to any one leg. There is no `fetched_at` — the client stamps
 * its own (see `terrain.ts`).
 */
export interface TerrainProfile {
  legs: TerrainLeg[];
  total_nm: number;
  max_terrain_ft: number | null;
  max_obstacle_ft: number | null;
  msa_ft: number | null;
  msa_rule: string;
  /** the step actually used — RAISED above the one asked for when the route is long */
  step_nm: number;
  corridor_nm: number;
  /** the required attribution string (Mapterhorn + openAIP) */
  source: string;
  /** stable codes, translated by the client and always shown */
  assumptions: string[];
}

export interface TerrainElevPoint {
  lat: number;
  lon: number;
  /** null = the archive has **no tile** here, which is NOT the same as sea level */
  elev_ft: number | null;
}

export interface TerrainElev {
  points: TerrainElevPoint[];
  source: string;
}

/**
 * 2–60 waypoints, ≤ 500 NM per leg and ≤ 3 000 NM in total; `corridor_nm` 1–10,
 * `step_nm` 0.25–5. A server with no `TERRAIN_PMTILES` answers 503 `unavailable`.
 * Public — no session needed, so a shared plan could ask too (it does not).
 */
export function fetchTerrainProfile(
  waypoints: readonly { lat: number; lon: number }[],
  opts: { corridorNm?: number; stepNm?: number; signal?: AbortSignal } = {},
): Promise<TerrainProfile> {
  const body: Record<string, unknown> = { waypoints: waypoints.map(w => ({ lat: w.lat, lon: w.lon })) };
  if (opts.corridorNm != null) body['corridor_nm'] = opts.corridorNm;
  if (opts.stepNm != null) body['step_nm'] = opts.stepNm;
  return requestSignal<TerrainProfile>('/terrain/profile', opts.signal, body);
}

/** 1–200 points, answered in order. Same 503 as the profile when the DEM is missing. */
export function fetchElevations(
  points: readonly { lat: number; lon: number }[],
  signal?: AbortSignal,
): Promise<TerrainElev> {
  const list = points.map(p => p.lat.toFixed(5) + ',' + p.lon.toFixed(5)).join(';');
  return requestSignal<TerrainElev>('/terrain/elev?points=' + encodeURIComponent(list), signal);
}

/**
 * `request` with an AbortSignal: the map cancels the previous search/check on
 * every keystroke and every waypoint drag, and an aborted fetch must not surface
 * as a network error banner.
 */
async function requestSignal<T>(path: string, signal?: AbortSignal, body?: unknown): Promise<T> {
  const init: RequestInit = { method: body === undefined ? 'GET' : 'POST', credentials: 'include' };
  if (signal) init.signal = signal;
  if (body !== undefined) {
    init.headers = { 'content-type': 'application/json' };
    init.body = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(BASE + path, init);
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiError(0, 'network', 'network error');
  }
  const payload = await readBody(res);
  if (!res.ok) {
    const e = errorFrom(res.status, payload);
    noteSessionState(path, e);
    throw e;
  }
  return payload as T;
}

/** True for the one "error" the map raises on purpose several times a second. */
export const isAbort = (e: unknown): boolean => e instanceof DOMException && e.name === 'AbortError';
